import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, CalendarOutlined,
    CheckCircleFilled, CloseCircleFilled
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const ManageHarvestSeason = () => {
    const [seasons, setSeasons]               = useState([]);
    const [loading, setLoading]               = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem]       = useState(null);
    const [addForm]  = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    /* ─── fetch ──────────────────────────────────────────────── */
    const fetchSeasons = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const res = await api.get(`/settings/harvestseason/list.php?page=${page}&limit=${pageSize}`);
            setSeasons(res.data.dataset.map(item => ({
                hsId:     item.hs_id,
                name:     item.name,
                isActive: item.is_active,
            })));
            setPagination({
                current:  res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page     || 10,
                total:    res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch harvest seasons.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSeasons(1, 10); }, [fetchSeasons]);
    const handleTableChange = (pg) => fetchSeasons(pg.current, pg.pageSize);

    /* ─── add ────────────────────────────────────────────────── */
    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/harvestseason/add.php', {
                name: values.name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Harvest season added successfully.");
                setIsAddModalVisible(false);
                fetchSeasons(pagination.current, pagination.pageSize);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add harvest season.");
        } finally { setLoading(false); }
    };

    /* ─── edit modal ─────────────────────────────────────────── */
    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({ name: record.name });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/harvestseason/edit.php', {
                hs_id: currentItem.hsId,
                name: values.name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Harvest season updated successfully.");
                setIsModalVisible(false);
                fetchSeasons(pagination.current, pagination.pageSize);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update harvest season.");
        } finally { setLoading(false); }
    };

    /* ─── toggle ─────────────────────────────────────────────── */
    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/settings/harvestseason/toggle.php', {
                hs_id:     record.hsId,
                is_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchSeasons(pagination.current, pagination.pageSize);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    /* ─── columns ────────────────────────────────────────────── */
    const columns = [
        {
            title: '#', key: 'index', width: 60,
            render: (_, __, idx) => (pagination.current - 1) * pagination.pageSize + idx + 1,
        },
        {
            title: 'Season Name', dataIndex: 'name', key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Status', dataIndex: 'isActive', key: 'isActive',
            align: 'center', width: 100,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Season">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.isActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.isActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    /* ─── render ─────────────────────────────────────────────── */
    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Settings' },
                                { title: 'Harvest Seasons' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <CalendarOutlined /> Harvest Seasons
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Manage harvest seasons used across harvest batches</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                {/* Table Card */}
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">List of Harvest Seasons</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                Add New Season
                            </Button>
                        </Space>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={seasons}
                            rowKey="hsId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 500 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Add Modal */}
            <Modal
                title="Add New Harvest Season"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={520}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Form.Item
                        label="Season Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please enter a season name' },
                            { max: 100, message: 'Max 100 characters' },
                        ]}
                    >
                        <Input placeholder="e.g. Maha" />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Season</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Harvest Season"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={520}
                destroyOnClose
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item
                        label="Season Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please enter a season name' },
                            { max: 100, message: 'Max 100 characters' },
                        ]}
                    >
                        <Input placeholder="e.g. Maha" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => { setIsModalVisible(false); editForm.resetFields(); }}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageHarvestSeason;